import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, ScoreBadge, StatusBadge } from "@/components/Common";
import AutopilotQueue from "@/components/AutopilotQueue";
import AIDiscoveryDialog from "@/components/AIDiscoveryDialog";
import { FinderOptionsDialog } from "@/components/FinderOptionsDialog";
import ContactNowBanner from "@/components/ContactNowBanner";
import { EmailPreviewButton } from "@/components/EmailPreviewDialog";
import { EmailComposer } from "@/components/EmailComposer";
import { AmbassadorContactsEditor } from "@/components/AmbassadorContactsEditor";
import { REGIONS } from "@/lib/italy";
import { normalizePhoneE164 } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import ErrorBoundary from "@/components/ErrorBoundary";
import { toast } from "sonner";
import {
  Users, Loader2, Sparkles, Instagram, Activity, MapPin, TrendingUp, Copy, Check,
  Send, Trash2, Globe, ExternalLink, MessageSquare, Eye, Archive, Pencil, Clock, History,
  Plus, Save, Mail, MessageCircle, Target, RefreshCw, Settings2, FileSpreadsheet,
} from "lucide-react";
import { ImportContactsDialog } from "@/components/ImportContactsDialog";
import { TranslateControls, LANG_LABEL } from "@/components/TranslateControls";
import { SortSelect, useSortOptions, sortRows, pickRecentId } from "@/components/SortSelect";
import { AmbQualBadge, VisualFitBadge, AmbDiscoveryReportPanel, AmbQualificationDetails, ExcludedAmbassadorsPanel } from "@/components/AmbassadorQualification";
import { MessageBody } from "@/components/MessageBody";
import { BulkCampaignComposer, ChannelToggle, contactWaNumber } from "@/components/BulkCampaignComposer";
import { CampaignBatchesPanel } from "@/components/CampaignBatchesPanel";

const DISCIPLINES = ["road bike", "gravel", "mountain bike", "e-bike", "moto adventure", "moto touring"];
const STATUSES = ["nuovo", "contattato", "in trattativa", "attivo", "salvato", "scartato"];
const STATUS_META = [
  { key: "nuovo", label: "Nuovo", dot: "bg-muted-foreground", text: "text-foreground" },
  { key: "contattato", label: "Contattato", dot: "bg-blue-500", text: "text-blue-600 dark:text-blue-400" },
  { key: "in trattativa", label: "In trattativa", dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
  { key: "attivo", label: "Attivo", dot: "bg-positive", text: "text-positive" },
  { key: "salvato", label: "Salvato", dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" },
  { key: "scartato", label: "Scartato", dot: "bg-red-500", text: "text-red-500 dark:text-red-400" },
];
const NATIONS = ["Italia", "Francia", "Spagna", "Germania", "Regno Unito", "Internazionale"];
const LANGUAGES = [
  { value: "auto", label: "Automatica (lingua del profilo)" },
  { value: "it", label: "Italiano" },
  { value: "en", label: "Inglese" },
  { value: "fr", label: "Francese" },
  { value: "es", label: "Spagnolo" },
  { value: "de", label: "Tedesco" },
];
const igUrlAmb = (h) => (h ? `https://instagram.com/${String(h).replace(/^@/, "")}` : "#");
const profileUrl = (amb) => {
  // Profili Strava (demo/simulati) non hanno una URL pubblica valida → nessun link Instagram fittizio
  if (amb?.platform === "strava") return (amb?.url && amb.url.includes("strava.com")) ? amb.url : "";
  return amb?.url || igUrlAmb(amb?.handle);
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

function AmbAvatar({ amb, size = 48 }) {
  const [broken, setBroken] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL;
  const src = broken ? null
    : amb?.avatar_path
      ? `${base}/api/ambassador-avatar?path=${encodeURIComponent(amb.avatar_path)}`
      : amb?.avatar_url
        ? `${base}/api/ambassador-avatar?url=${encodeURIComponent(amb.avatar_url)}`
        : null;
  const initials = ((amb?.name || amb?.handle || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join("") || "?").toUpperCase();
  const style = { width: size, height: size };
  if (src) {
    return <img src={src} alt={amb?.name || "ambassador"} style={style} onError={() => setBroken(true)}
      className="shrink-0 rounded-full bg-brema-bg object-cover ring-2 ring-white/10" data-testid="amb-avatar-img" />;
  }
  return (
    <div data-testid="amb-avatar-initials" style={style}
      className="flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand/30 to-brand/10 font-bold text-brand ring-2 ring-white/10">
      <span style={{ fontSize: Math.max(11, Math.round(size * 0.36)) }}>{initials}</span>
    </div>
  );
}

function ProfileHandle({ amb, className = "", testid, children }) {
  const url = profileUrl(amb);
  const content = children ?? (<>{amb?.platform === "strava" ? <Activity className="h-3 w-3 shrink-0" /> : <Instagram className="h-3 w-3 shrink-0" />}<span className="truncate">@{amb?.handle || "n-d"}</span></>);
  if (!url) return <span className={className} data-testid={testid}>{content}</span>;
  return <a href={url} target="_blank" rel="noopener noreferrer" className={className} data-testid={testid}>{content}</a>;
}

export default function Ambassadors() {
  const [tab, setTab] = useState("ricerca");
  const [platform, setPlatform] = useState("instagram");
  const [discipline, setDiscipline] = useState("road bike");
  const [disciplines, setDisciplines] = useState(DISCIPLINES.map((d) => ({ value: d, label: d })));
  const [discMgr, setDiscMgr] = useState(false);
  const loadDisciplines = () => api.get("/admin/finder-options?group=ambassador_discipline").then(({ data }) => { if (data.options?.length) setDisciplines(data.options); }).catch(() => {});
  useEffect(() => { loadDisciplines(); }, []);
  const [nation, setNation] = useState("Italia");
  const [region, setRegion] = useState("");
  const [count, setCount] = useState(5);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [justArchived, setJustArchived] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [discoverProgress, setDiscoverProgress] = useState(null);
  const [lastReport, setLastReport] = useState(null);
  const [excludedAmb, setExcludedAmb] = useState([]);
  const loadExcluded = () => api.get("/ambassadors/excluded").then(({ data }) => setExcludedAmb(data.excluded || [])).catch(() => {});
  useEffect(() => { loadExcluded(); }, []);
  const reevaluateExcluded = async (e) => {
    try { await api.delete(`/ambassadors/excluded/${e._id}`); setExcludedAmb((p) => p.filter((x) => x._id !== e._id)); toast.success(`@${e.handle} rimosso dagli esclusi: potrà essere riproposto`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const clearExcluded = async () => {
    if (!window.confirm(`Svuotare il registro esclusi (${excludedAmb.length} profili)? Potranno essere riproposti dalle prossime ricerche.`)) return;
    try { const { data } = await api.post("/ambassadors/excluded/clear"); toast.success(`${data.deleted} esclusi rimossi`); setExcludedAmb([]); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const [list, setList] = useState([]);
  const [integrations, setIntegrations] = useState(null);

  // Single-contact dialog (DM) + email composer
  const [dialogAmb, setDialogAmb] = useState(null);
  const [composerAmb, setComposerAmb] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const [msgLanguage, setMsgLanguage] = useState("auto");
  const [contactChannel, setContactChannel] = useState("instagram");
  const [composerChannel, setComposerChannel] = useState("email");
  const [waTemplates, setWaTemplates] = useState([]);
  const [ambTemplates, setAmbTemplates] = useState([]);
  const [igTemplateId, setIgTemplateId] = useState("");

  // Manual ambassador addition
  const emptyManualAmb = { name: "", handle: "", email: "", whatsapp: "", discipline: "road bike", nation: "Italia", followers: "", notes: "" };
  const [manualOpen, setManualOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualAmb, setManualAmb] = useState(emptyManualAmb);
  const setMA = (patch) => setManualAmb((p) => ({ ...p, ...patch }));
  const [lookupName, setLookupName] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupNote, setLookupNote] = useState("");
  const [lookupVerified, setLookupVerified] = useState(true);

  // Assisted mass outreach
  const [selected, setSelected] = useState(new Set());
  const [bulkLanguage, setBulkLanguage] = useState("auto");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkItems, setBulkItems] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [bulkAction, setBulkAction] = useState(null); // null | "archive" | "delete"
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(new Set());
  const [refreshingAll, setRefreshingAll] = useState(false);

  // Contattati table (notes drafts)
  const [notesDraft, setNotesDraft] = useState({});

  // Campaigns (storico)
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDetail, setCampaignDetail] = useState(null); // {campaign, messages}

  // Profile detail
  const [profileDetail, setProfileDetail] = useState(null); // {ambassador, messages}
  const [profileLoading, setProfileLoading] = useState(false);

  // Saved table: sort / filters + delete + manual message save
  const [savedSort, setSavedSort] = useState("");
  const [savedSel, setSavedSel] = useState(new Set());
  const [savedBulkChannel, setSavedBulkChannel] = useState("email");
  const [savedBulkOpen, setSavedBulkOpen] = useState(false);
  const [batchesRefresh, setBatchesRefresh] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  useEffect(() => { api.get("/campaign-batches?target=ambassadors").then(({ data }) => setBatchCount((data.batches || []).length)).catch(() => {}); }, [batchesRefresh]);
  const [resultsSort, setResultsSort] = useState("");
  const { options: sortOptions, catalog: sortCatalog } = useSortOptions("ambassador");
  const [filterDiscipline, setFilterDiscipline] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [ambToDelete, setAmbToDelete] = useState(null);
  const [deletingAmb, setDeletingAmb] = useState(false);
  const [savingMsg, setSavingMsg] = useState(false);

  const load = () => { api.get("/ambassadors").then(({ data }) => setList(data.ambassadors)).catch(() => {}); };
  const loadCampaigns = () => { api.get("/ambassador-campaigns").then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {}); };
  const triggerAvatars = () => {
    api.post("/ambassadors/refresh-avatars").then(({ data }) => {
      if ((data.queued || 0) > 0 || data.running) {
        let n = 0;
        const iv = setInterval(() => { n += 1; load(); if (n >= 6) clearInterval(iv); }, 6000);
      }
    }).catch(() => {});
  };
  useEffect(() => {
    load();
    loadCampaigns();
    api.get("/integrations/status").then(({ data }) => setIntegrations(data)).catch(() => {});
    api.get("/message-templates?channel=whatsapp").then(({ data }) => setWaTemplates(data.templates || [])).catch(() => {});
    api.get("/send-templates?module=ambassador").then(({ data }) => setAmbTemplates(data.templates || [])).catch(() => {});
    triggerAvatars();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const applyIgTemplate = (id) => {
    const t = ambTemplates.find((x) => x._id === id);
    if (!t) return;
    setIgTemplateId(id);
    const a = dialogAmb || {};
    const vars = { nome: a.name || "", name: a.name || "", citta: "", città: "", regione: a.region || a.nation || "", nazione: a.nation || "", categoria: a.discipline || "" };
    const text = (t.body || "").replace(/\{(\w+|città)\}/gi, (m, k) => (k.toLowerCase() in vars ? vars[k.toLowerCase()] : m));
    setMessage(text);
    toast.success("Modello caricato · puoi modificarlo prima di copiarlo");
  };

  const contactNext = () => {
    setJustArchived((prev) => {
      if (!prev.length) return prev;
      const [first, ...rest] = prev;
      setComposerChannel(first.email || !first.whatsapp ? "email" : "whatsapp");
      setComposerAmb(first);
      return rest;
    });
  };

  useEffect(() => { setRegion(""); }, [nation]);

  const pollDiscoverJob = (jobId) => new Promise((resolve, reject) => {
    let tries = 0, previewLoaded = false;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/discover-jobs/${jobId}`);
        const j = data.job;
        if (j.progress) {
          setDiscoverProgress(j.progress);
          if (j.progress.preview_ready && !previewLoaded) { previewLoaded = true; load(); }
        }
        if (j.status === "ready") { clearInterval(iv); resolve(j.result || {}); }
        else if (j.status === "error") { clearInterval(iv); reject(new Error(j.error || "Ricerca fallita")); }
        else if (tries > 420) { clearInterval(iv); reject(new Error("La ricerca sta impiegando troppo tempo. Riprova.")); }
      } catch (e) { if (tries > 420) { clearInterval(iv); reject(e); } }
    }, 2500);
  });

  const discover = async () => {
    setDiscovering(true);
    setDiscoverProgress({ phase: "starting" });
    try {
      const excludeIds = list.map((a) => a.dedup_key).filter(Boolean);
      const { data: init } = await api.post("/ambassadors/discover-async", {
        platform, discipline, count: Number(count),
        nation, region: nation === "Italia" ? region : "", exclude_ids: excludeIds,
      });
      const result = await pollDiscoverJob(init.job_id);
      const newCount = result.new_count ?? result.count ?? 0;
      const req = result.requested || Number(count);
      if (result.report) setLastReport(result.report);
      loadExcluded();
      if (result.engine === "brema-ambassador-qualifier") {
        const q = result.qualified_count ?? newCount, v = result.to_verify_count ?? 0, ex = result.excluded_count ?? 0;
        if (q === 0) {
          toast.info(`Nessun Ambassador qualificato (≥${result.report?.threshold ?? 70}): ${result.report?.analyzed ?? 0} analizzati · ${v} da verificare · ${ex} esclusi`);
        } else {
          toast.success(`${q}/${req} Ambassador qualificati · ${v} da verificare · ${ex} esclusi${q < req ? (result.report?.limit_reached ? " · limite analisi raggiunto" : result.report?.time_budget_reached ? " · tempo massimo raggiunto" : " · bacino esaurito") : ""}`);
        }
      } else if (newCount === 0) {
        toast.info(result.exhausted
          ? `Nessun altro profilo nuovo per "${discipline}". Prova un'altra disciplina o zona.`
          : `Nessun nuovo profilo individuato. Riprova.`);
      } else {
        let msg = `+${newCount} nuovi ambassador individuati`;
        if (newCount < req) msg += result.exhausted ? ` · pool esaurito (${newCount}/${req})` : ` (${newCount}/${req} richiesti)`;
        toast.success(msg);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setDiscovering(false); setDiscoverProgress(null); }
  };

  const clearAmbassadors = async () => {
    if (!window.confirm(`Vuoi svuotare i risultati di ricerca (${resultsList.length} profili)? Gli ambassador già salvati in "Ambassador Salvati" non vengono toccati.`)) return;
    try {
      const { data } = await api.post("/ambassadors/discard-results", { ambassador_ids: [] });
      toast.success(`${data.removed} risultati rimossi · archivio Ambassador Salvati intatto`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const deleteAmbassador = async () => {
    if (!ambToDelete?._id) return;
    setDeletingAmb(true);
    try {
      await api.delete(`/ambassadors/${ambToDelete._id}`);
      toast.success("Ambassador eliminato");
      setList((prev) => prev.filter((x) => x._id !== ambToDelete._id));
      setSelected((prev) => { const n = new Set(prev); n.delete(ambToDelete._id); return n; });
      if (profileDetail?.ambassador?._id === ambToDelete._id) setProfileDetail(null);
      setAmbToDelete(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setDeletingAmb(false); }
  };

  const changeStatus = async (amb, status) => {
    setList((prev) => prev.map((a) => (a._id === amb._id ? { ...a, status } : a)));
    setProfileDetail((p) => (p && p.ambassador._id === amb._id ? { ...p, ambassador: { ...p.ambassador, status } } : p));
    try { await api.patch(`/ambassadors/${amb._id}`, { status }); }
    catch { toast.error("Aggiornamento fallito"); load(); }
  };

  const saveNotes = async (amb, notes) => {
    if ((amb.notes || "") === (notes || "")) return;
    setList((prev) => prev.map((a) => (a._id === amb._id ? { ...a, notes } : a)));
    try { await api.patch(`/ambassadors/${amb._id}`, { notes }); toast.success("Note salvate"); }
    catch { toast.error("Salvataggio note fallito"); load(); }
  };

  const openMessage = (amb) => {
    setDialogAmb(amb); setMessage(""); setCopied(false); setMsgLanguage("auto"); setIgTemplateId("");
    setContactChannel(amb?.email ? "email" : (amb?.whatsapp ? "whatsapp" : "instagram"));
  };

  const copyForDM = async () => {
    if (!message.trim()) { toast.error("Genera o scrivi prima il messaggio"); return; }
    try { await navigator.clipboard.writeText(message); } catch { /* ignore */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
    // Salva nello storico e apri il profilo Instagram in una nuova scheda
    try { await api.post("/ambassadors/save-message", { ambassador_id: dialogAmb._id, message, language: msgLanguage }); load(); loadCampaigns(); } catch { /* ignore */ }
    const url = profileUrl(dialogAmb);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Testo copiato · profilo Instagram aperto: incolla il DM");
  };

  const generateMessage = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ambassadors/generate-message", {
        ambassador_id: dialogAmb._id, language: msgLanguage,
      });
      setMessage(data.message);
      toast.success("Bozza AI generata · puoi modificarla prima di salvarla");
      load(); loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  const saveManualMessage = async () => {
    if (!dialogAmb?._id || !message.trim()) return;
    setSavingMsg(true);
    try {
      await api.post("/ambassadors/save-message", {
        ambassador_id: dialogAmb._id, message, language: msgLanguage,
      });
      toast.success("Messaggio salvato nello storico del profilo");
      setDialogAmb(null);
      load(); loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingMsg(false); }
  };

  const runAmbLookup = async () => {
    const q = lookupName.trim();
    if (!q) { toast.error("Digita il nome della persona da cercare"); return; }
    setLookingUp(true);
    setLookupNote("");
    setLookupVerified(true);
    try {
      const { data } = await api.post("/ambassadors/lookup", { name: q, platform, discipline: manualAmb.discipline || "road bike", nation: manualAmb.nation || "Italia" });
      const p = data.preview || {};
      setMA({ name: p.name || q, handle: p.handle || "", followers: p.followers || "", email: p.email || "", discipline: p.discipline || manualAmb.discipline, nation: p.nation || manualAmb.nation, notes: p.notes || "" });
      setLookupNote(data.note || "");
      setLookupVerified(data.verified !== false);
      if (data.found) toast.success("Profilo compilato dall'AI · controlla e salva");
      else toast("Profilo non trovato online · compila manualmente");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLookingUp(false); }
  };

  const submitManualAmbassador = async () => {
    if (!manualAmb.name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    setManualSaving(true);
    try {
      const { data } = await api.post("/ambassadors/manual", {
        ...manualAmb, followers: Number(manualAmb.followers) || 0,
      });
      toast.success(data.created ? "Ambassador aggiunto alla lista" : (data.note || "Ambassador già presente"));
      setManualOpen(false);
      setManualAmb(emptyManualAmb);
      setTab("ricerca");
      load();
      if (data.created && data.ambassador) { setJustArchived([data.ambassador]); setTimeout(triggerAvatars, 1500); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setManualSaving(false); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === displayList.length ? new Set() : new Set(displayList.map((a) => a._id))));
  };

  const archiveSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkActionLoading(true);
    try {
      const { data } = await api.post("/ambassadors/save-selected", { ambassador_ids: ids });
      toast.success(`${data.saved} ambassador salvati in Ambassador Salvati`, {
        action: { label: "Vedi Ambassador Salvati", onClick: () => { setTab("contattati"); setSavedSort(pickRecentId(sortOptions)); } },
      });
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBulkActionLoading(false); setBulkAction(null); }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkActionLoading(true);
    try {
      const { data } = await api.post("/ambassadors/discard-results", { ambassador_ids: ids });
      toast.success(`${data.removed} profili rimossi dai risultati · archivio intatto`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBulkActionLoading(false); setBulkAction(null); }
  };

  const refreshOneAvatar = async (amb) => {
    if (!amb?._id) return;
    setAvatarBusy((prev) => new Set(prev).add(amb._id));
    try {
      await api.post("/ambassadors/refresh-avatars", { ids: [amb._id], force: true });
      toast("Recupero foto profilo avviato…");
      let n = 0;
      const iv = setInterval(() => {
        n += 1; load();
        if (n >= 5) { clearInterval(iv); setAvatarBusy((prev) => { const s = new Set(prev); s.delete(amb._id); return s; }); }
      }, 4000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setAvatarBusy((prev) => { const s = new Set(prev); s.delete(amb._id); return s; });
    }
  };

  const refreshAllAvatars = async () => {
    setRefreshingAll(true);
    try {
      // force=true, nessun id → recupera SOLO i profili senza foto (contiene i crediti Apify)
      const { data } = await api.post("/ambassadors/refresh-avatars", { force: true });
      const queued = data.queued || 0;
      if (queued === 0 && !data.running) {
        toast.success("Tutte le foto sono già aggiornate");
        setRefreshingAll(false);
        return;
      }
      toast.success(`Recupero foto avviato per ${queued} profili senza avatar…`);
      let n = 0;
      const iv = setInterval(() => {
        n += 1; load();
        if (n >= 8) { clearInterval(iv); setRefreshingAll(false); }
      }, 5000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setRefreshingAll(false);
    }
  };


  const startBulkOutreach = async () => {
    if (selected.size === 0) return;
    setBulkOpen(true);
    setBulkLoading(true);
    setBulkItems([]);
    try {
      const { data } = await api.post("/ambassadors/bulk-message", {
        ambassador_ids: Array.from(selected), language: bulkLanguage,
        discipline, nation, region: nation === "Italia" ? region : "",
      });
      setBulkItems(data.items || []);
      toast.success(`${data.generated}/${data.total} messaggi generati · campagna salvata nello storico`);
      load(); loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBulkOpen(false);
    } finally { setBulkLoading(false); }
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text || "");
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const openProfile = async (amb) => {
    setProfileLoading(true);
    setProfileDetail({ ambassador: amb, messages: [] });
    try {
      const { data } = await api.get(`/ambassadors/${amb._id}`);
      setProfileDetail({ ambassador: data.ambassador, messages: data.messages || [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setProfileLoading(false); }
  };

  const openCampaign = async (c) => {
    setCampaignDetail({ campaign: c, messages: [] });
    try {
      const { data } = await api.get(`/ambassador-campaigns/${c._id}`);
      setCampaignDetail({ campaign: data.campaign, messages: data.messages || [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const renameCampaign = async (c) => {
    const title = window.prompt("Nuovo nome campagna:", c.title || "");
    if (title === null) return;
    const t = title.trim();
    if (!t) return;
    try {
      await api.patch(`/ambassador-campaigns/${c._id}`, { title: t });
      setCampaigns((prev) => prev.map((x) => (x._id === c._id ? { ...x, title: t } : x)));
      setCampaignDetail((d) => (d && d.campaign._id === c._id ? { ...d, campaign: { ...d.campaign, title: t } } : d));
      toast.success("Nome campagna aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Eliminare la campagna "${c.title}" dallo storico?`)) return;
    try {
      await api.delete(`/ambassador-campaigns/${c._id}`);
      setCampaigns((prev) => prev.filter((x) => x._id !== c._id));
      if (campaignDetail?.campaign._id === c._id) setCampaignDetail(null);
      toast.success("Campagna eliminata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const resultsList = useMemo(() => list.filter((a) => a.search_staged === true), [list]);
  const savedList = useMemo(() => list.filter((a) => a.search_staged !== true), [list]);
  const activeList = useMemo(() => sortRows(resultsList, resultsSort, sortOptions, sortCatalog), [resultsList, resultsSort, sortOptions, sortCatalog]);
  const displayList = useMemo(
    () => (genderFilter === "all" ? activeList : activeList.filter((a) => (a.gender || "") === genderFilter)),
    [activeList, genderFilter]);
  const allSelected = displayList.length > 0 && selected.size === displayList.length;
  const showImported = () => {
    setFilterDiscipline("all"); setFilterStatus("all"); setGenderFilter("all"); setSavedSort(pickRecentId(sortOptions));
    setTab("contattati");
    load();
    toast.success("Ambassador Salvati aggiornato: i nuovi profili sono in cima alla lista");
  };
  const savedRows = useMemo(() => {
    let rows = [...savedList];
    if (filterDiscipline !== "all") rows = rows.filter((a) => (a.discipline || "").toLowerCase() === filterDiscipline);
    if (filterStatus !== "all") rows = rows.filter((a) => (a.status || "nuovo") === filterStatus);
    if (genderFilter !== "all") rows = rows.filter((a) => (a.gender || "") === genderFilter);
    return sortRows(rows, savedSort, sortOptions, sortCatalog);
  }, [savedList, filterDiscipline, filterStatus, genderFilter, savedSort, sortOptions, sortCatalog]);
  const savedSelCount = savedRows.filter((a) => savedSel.has(a._id)).length;
  const savedAllSelected = savedRows.length > 0 && savedRows.every((a) => savedSel.has(a._id));
  const savedBulkRecipients = savedRows.filter((a) => savedSel.has(a._id));
  const toggleSavedSel = (id) => setSavedSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSavedAll = () => setSavedSel(savedAllSelected ? new Set() : new Set(savedRows.map((a) => a._id)));
  const selectSavedOnlyWa = () => setSavedSel(new Set(savedRows.filter((a) => contactWaNumber(a)).map((a) => a._id)));
  const selectSavedOnlyEmail = () => setSavedSel(new Set(savedRows.filter((a) => /^\S+@\S+\.\S+$/.test(String(a.email || "").trim())).map((a) => a._id)));
  const statusCounts = useMemo(() => {
    const c = { nuovo: 0, contattato: 0, "in trattativa": 0, attivo: 0, salvato: 0, scartato: 0 };
    list.forEach((a) => { const s = a.status || "nuovo"; if (c[s] !== undefined) c[s] += 1; });
    return c;
  }, [list]);

  return (
    <div>
      <PageHeader
        eyebrow="Plancia di comando Brand"
        title="Ambassador & Marketing Finder"
        subtitle="Ricerca micro-influencer bike/moto, gestisci stato e accordi, e tieni lo storico delle campagne di contatto."
      />

      <div className="mb-8">
        <AutopilotQueue module="ambassador" onSaved={{ reload: load, navigate: showImported }} />
      </div>

      <ContactNowBanner items={justArchived} label="ambassador" onContact={contactNext} onDismiss={() => setJustArchived([])} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="ricerca" data-testid="amb-tab-ricerca"><Users className="mr-1.5 h-4 w-4" /> Ricerca & Lista ({resultsList.length})</TabsTrigger>
          <TabsTrigger value="contattati" data-testid="amb-tab-contattati"><Archive className="mr-1.5 h-4 w-4" /> Ambassador Salvati ({savedList.length})</TabsTrigger>
          <TabsTrigger value="storico" data-testid="amb-tab-storico"><History className="mr-1.5 h-4 w-4" /> Storico Campagne ({campaigns.length + batchCount})</TabsTrigger>
        </TabsList>

        {/* ================= TAB RICERCA & LISTA ================= */}
        <TabsContent value="ricerca">
          <div className="mb-8 rounded-2xl border border-border bg-brema-card p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-foreground">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-brand" />
                <h3 className="whitespace-nowrap font-semibold">Ricerca ambassador</h3>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" data-testid="import-ambassadors-btn" onClick={() => setImportOpen(true)}
                  className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" /> Importa CSV/Excel
                </Button>
                <Button size="sm" data-testid="add-manual-ambassador-btn" onClick={() => { setManualAmb(emptyManualAmb); setLookupName(""); setLookupNote(""); setManualOpen(true); }}
                  className="h-9 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Aggiungi ambassador
                </Button>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 lg:items-end">
              <div className="space-y-1.5">
                <Label>Piattaforma</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger data-testid="platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="strava">Strava</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Disciplina</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground" onClick={() => setDiscMgr(true)} data-testid="manage-ambassador-discipline-btn"><Settings2 className="mr-1 h-3.5 w-3.5" /> Gestisci</Button>
                </div>
                <Select value={discipline} onValueChange={setDiscipline}>
                  <SelectTrigger data-testid="discipline-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {disciplines.map((d) => <SelectItem key={d.value} value={d.value} className="capitalize">{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FinderOptionsDialog group="ambassador_discipline" title="Disciplina — Ambassador Finder" open={discMgr} onOpenChange={setDiscMgr} onChanged={loadDisciplines} />
              </div>
              <div className="space-y-1.5">
                <Label>Nazione</Label>
                <Select value={nation} onValueChange={setNation}>
                  <SelectTrigger data-testid="nation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {NATIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Regione {nation !== "Italia" && <span className="text-muted-foreground">(solo Italia)</span>}</Label>
                <Select value={region || "all"} onValueChange={(v) => setRegion(v === "all" ? "" : v)} disabled={nation !== "Italia"}>
                  <SelectTrigger data-testid="region-select"><SelectValue placeholder="Tutte le regioni" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all">Tutte le regioni</SelectItem>
                    {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Quanti (max 10)</Label>
                <Input type="number" min={1} max={10} value={count} data-testid="amb-count-input"
                  onChange={(e) => setCount(e.target.value)} />
              </div>
              <Button onClick={discover} disabled={discovering} data-testid="amb-discover-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-primary font-semibold leading-none text-primary-foreground hover:bg-primary/90">
                {discovering ? <><Loader2 className="h-4 w-4 animate-spin" /> Ricerca…</> : <><Sparkles className="h-4 w-4" /> Trova</>}
              </Button>
              <Button onClick={() => setDiscoveryOpen(true)} variant="outline" data-testid="ai-discovery-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full border-primary/40 font-semibold leading-none text-primary hover:bg-primary/10">
                <Target className="h-4 w-4" /> AI Discovery Search
              </Button>
            </div>
            <AIDiscoveryDialog open={discoveryOpen} onOpenChange={setDiscoveryOpen}
              title="AI Discovery Search · Ambassador"
              searchEndpoint="/ambassadors/discovery-search" archiveEndpoint="/ambassadors/discovery-archive"
              searchBody={{ nation, region, discipline, platform, count: Number(count) || 8 }}
              getKey={(c, i) => c.dedup_key || c.handle || String(i)}
              getWhy={(c) => c.why_match}
              renderSummary={(data) => data.report ? <AmbDiscoveryReportPanel report={data.report} compact /> : null}
              renderCandidate={(c) => (
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{c.name}</span>
                    {c.handle && (
                      <a href={c.url || `https://instagram.com/${String(c.handle).replace(/^@/, "")}`}
                        target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                        data-testid="discovery-handle-link"
                        className="text-xs text-primary hover:underline">@{c.handle}</a>
                    )}
                    {c.gender && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.gender === "donna" ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"}`}>{c.gender === "donna" ? "Donna" : "Uomo"}</span>}
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">Fit {c.fit_score}</span>
                    <AmbQualBadge amb={c} testid="discovery-amb-qual-badge" />
                    <VisualFitBadge amb={c} testid="discovery-amb-visual-badge" />
                  </div>
                  <div className="text-xs text-muted-foreground">{[c.location, c.discipline].filter(Boolean).join(" · ")}{c.followers ? ` · ${Number(c.followers).toLocaleString("it-IT")} follower` : ""}</div>
                </div>
              )}
              onArchived={(data) => { load(); setJustArchived(data?.ambassadors || []); setTimeout(triggerAvatars, 1500); }} />
            {discovering && (
              <div className="mt-3 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-brand" data-testid="amb-discover-progress">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {discoverProgress?.phase === "searching" ? (
                  <span>Discovery ampia (web + hashtag) di rider e content creator{discoverProgress.step_type === "related_discipline" ? ` · ampliamento a "${discoverProgress.step_discipline}"` : discoverProgress.step_type === "region" ? ` · ampliamento regionale: ${discoverProgress.current_area}` : discoverProgress.current_area ? ` · ${discoverProgress.current_area}` : ""}{discoverProgress.cycle ? ` · ciclo ${discoverProgress.cycle}` : ""}…</span>
                ) : discoverProgress?.phase === "compiling" ? (
                  <span>Deduplica e pre-selezione dei candidati reali{discoverProgress.candidates ? ` · ${discoverProgress.candidates} candidati` : ""}…</span>
                ) : discoverProgress?.phase === "verifying" ? (
                  <span>Verifica esistenza degli handle{discoverProgress.candidates ? ` · ${discoverProgress.candidates} profili` : ""}…</span>
                ) : discoverProgress?.phase === "profiles" ? (
                  <span>Dati reali del profilo (follower, post, attività) via Apify{discoverProgress.candidates ? ` · ${discoverProgress.candidates} profili` : ""}…</span>
                ) : discoverProgress?.phase === "visual" ? (
                  <span>Analisi visiva reale dei contenuti recenti (modello vision){discoverProgress.candidates ? ` · ${discoverProgress.candidates} profili sopravvissuti ai filtri` : ""}…</span>
                ) : discoverProgress?.phase === "qualifying" ? (
                  <span>Qualificazione con Knowledge Base ed esempi positivi{discoverProgress.candidates ? ` · ${discoverProgress.candidates} profili` : ""}…</span>
                ) : discoverProgress?.phase === "analyzing" || discoverProgress?.phase === "done" ? (
                  <span data-testid="amb-discover-progress-counts">Qualificati <b>{discoverProgress.qualified ?? 0}/{discoverProgress.target ?? count}</b> · {discoverProgress.to_verify ?? 0} da verificare · {discoverProgress.excluded ?? 0} esclusi · {discoverProgress.analyzed ?? 0} analizzati</span>
                ) : discoverProgress?.phase === "enriching" ? (
                  <span>Profili qualificati · recupero contatti (social/sito/email) in sottofondo…</span>
                ) : (
                  <span>Avvio ricerca AI…</span>
                )}
              </div>
            )}
            {lastReport && !discovering && (
              <div className="mt-3"><AmbDiscoveryReportPanel report={lastReport} compact onClose={() => setLastReport(null)} /></div>
            )}
            <p className="mt-4 text-xs text-muted-foreground">
              {integrations?.brave || integrations?.google_cse || integrations?.perplexity ? (
                <span className="flex items-center gap-1.5 text-positive" data-testid="amb-integration-real">
                  <Check className="h-3.5 w-3.5" /> Pipeline unica Ambassador attiva · discovery web + hashtag → verifica handle → dati reali Apify → hard filter → analisi visiva reale (GPT-5.4) → qualificazione Claude con Knowledge Base ed esempi positivi → soglia {lastReport?.threshold ?? 70}/100
                </span>
              ) : (
                <span data-testid="amb-integration-demo">
                  Modalità dimostrativa: profili generati dall'AI. Configura una Web Search (Brave/Google CSE) per la ricerca reale.
                </span>
              )}
            </p>
          </div>

          {resultsList.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="amb-bulk-toolbar">
              <Button variant="secondary" size="sm" data-testid="amb-select-all-btn" onClick={toggleSelectAll}
                className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
              </Button>
              <span className="text-sm text-muted-foreground" data-testid="amb-selected-counter">
                {selected.size} selezionati
              </span>
              <SortSelect options={sortOptions} value={resultsSort} onChange={setResultsSort} testid="amb-results-sort-select" className="h-9 w-56 text-xs" />
              <div className="flex items-center gap-1 rounded-full border border-border bg-brema-bg p-0.5" data-testid="amb-gender-filter">
                {[["all", "Tutti"], ["donna", "Donne"], ["uomo", "Uomini"]].map(([v, lbl]) => (
                  <button key={v} data-testid={`amb-gender-${v}`} onClick={() => setGenderFilter(v)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${genderFilter === v ? "bg-selected text-selected-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              <Button variant="secondary" size="sm" data-testid="amb-refresh-all-avatars-btn"
                onClick={refreshAllAvatars} disabled={refreshingAll}
                className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                {refreshingAll
                  ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Recupero foto…</>
                  : <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Aggiorna tutte le foto</>}
              </Button>
              {selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={bulkLanguage} onValueChange={setBulkLanguage}>
                    <SelectTrigger className="h-9 w-56" data-testid="bulk-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" data-testid="amb-bulk-outreach-btn" onClick={startBulkOutreach}
                    className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                    <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Avvia contatto massivo
                  </Button>
                  <Button size="sm" data-testid="amb-bulk-archive-btn" onClick={archiveSelected} disabled={bulkActionLoading}
                    className="rounded-full border border-brand bg-brand/10 text-xs font-semibold text-brand hover:bg-brand/20">
                    {bulkActionLoading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />} Salva selezionati in Ambassador Salvati
                  </Button>
                  <Button size="sm" variant="danger" data-testid="amb-bulk-delete-btn" onClick={() => setBulkAction("delete")}
                    className="rounded-full text-xs">
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Rimuovi dai risultati
                  </Button>
                </div>
              )}
              <Button variant="danger" size="sm" data-testid="clear-ambassadors-btn" onClick={clearAmbassadors}
                className="ml-auto rounded-full text-xs">
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Svuota risultati
              </Button>
            </div>
          )}

          {resultsList.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground" data-testid="amb-results-empty">
              Nessun risultato di ricerca. I profili trovati da "Trova" e da AI Discovery compaiono qui e restano temporanei finché non li salvi in <span className="font-semibold text-foreground">Ambassador Salvati</span>.
            </div>
          ) : (
            <ErrorBoundary>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {displayList.map((amb, i) => {
                  const isSel = selected.has(amb?._id);
                  return (
                    <motion.div
                      key={amb?._id || i}
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
                      data-testid="ambassador-card"
                      className={`flex min-w-0 flex-col rounded-xl border bg-brema-card p-5 transition-colors ${isSel ? "border-primary/70" : "border-border"}`}
                    >
                      <div className="mb-3 flex min-w-0 items-start gap-3">
                        <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(amb?._id)}
                          data-testid="amb-select-checkbox" className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                        <div className="relative shrink-0">
                          <AmbAvatar amb={amb} size={48} />
                          {!amb?.avatar_path && (
                            <button type="button" onClick={() => refreshOneAvatar(amb)} disabled={avatarBusy.has(amb?._id)}
                              title="Aggiorna foto profilo" data-testid="amb-avatar-refresh-btn"
                              className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow ring-2 ring-brema-card transition-colors hover:bg-primary/90 disabled:opacity-70">
                              {avatarBusy.has(amb?._id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                            </button>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <h4 className="truncate font-semibold text-foreground">{amb?.name || "Ambassador"}</h4>
                            {amb?.gender && (
                              <span data-testid="amb-gender-badge"
                                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${amb.gender === "donna" ? "bg-pink-500/20 text-pink-800 dark:text-pink-200 border border-pink-500/40" : "bg-sky-500/20 text-sky-800 dark:text-sky-200 border border-sky-500/40"}`}>
                                {amb.gender === "donna" ? "Donna" : "Uomo"}
                              </span>
                            )}
                          </div>
                          <ProfileHandle amb={amb} testid="amb-handle-link"
                            className="flex min-w-0 max-w-full items-center gap-1 text-xs text-foreground hover:text-brand hover:underline" />
                        </div>
                        <ScoreBadge score={amb?.fit_score} />
                      </div>
                      {amb?.qualification && (
                        <div className="mb-2 flex flex-wrap gap-1.5" data-testid="amb-card-qualification">
                          <AmbQualBadge amb={amb} />
                          <VisualFitBadge amb={amb} />
                        </div>
                      )}
                      <p className="mb-3 text-sm text-foreground">{amb?.bio || "—"}</p>
                      <div className="mb-3 grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-lg bg-brema-bg py-2">
                          <p className="text-sm font-bold text-foreground">{Number(amb?.followers || 0).toLocaleString("it-IT")}</p>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Follower</p>
                        </div>
                        <div className="rounded-lg bg-brema-bg py-2">
                          <p className="flex items-center justify-center gap-0.5 text-sm font-bold text-brand">
                            <TrendingUp className="h-3 w-3" />{amb?.engagement_rate ?? 0}%
                          </p>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Engag.</p>
                        </div>
                        <div className="rounded-lg bg-brema-bg py-2">
                          <p className="flex items-center justify-center gap-0.5 text-xs font-semibold text-foreground">
                            <MapPin className="h-3 w-3" />{amb?.location?.split(",")[0] || amb?.region || amb?.nation || "N/D"}
                          </p>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Zona</p>
                        </div>
                      </div>
                      <div className="mb-4 flex flex-wrap gap-1.5">
                        {(Array.isArray(amb?.why_match) ? amb.why_match : []).map((w, idx) => (
                          <span key={idx} className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground">{w}</span>
                        ))}
                      </div>
                      <div className="mt-auto flex items-center gap-2">
                        <Select value={amb?.status || "nuovo"} onValueChange={(v) => changeStatus(amb, v)}>
                          <SelectTrigger className="h-9 flex-1" data-testid="amb-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="secondary" data-testid="amb-detail-btn" onClick={() => openProfile(amb)}
                          className="rounded-full border border-border bg-transparent hover:bg-brema-hover">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" data-testid="amb-message-btn" onClick={() => openMessage(amb)}
                          className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                          <Send className="mr-1 h-3.5 w-3.5" /> Contatta
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </ErrorBoundary>
          )}
          <ExcludedAmbassadorsPanel items={excludedAmb} onReevaluate={reevaluateExcluded} onClear={clearExcluded} />
        </TabsContent>

        {/* ================= TAB AMBASSADOR SALVATI/CONTATTATI ================= */}
        <TabsContent value="contattati">
          {savedList.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground" data-testid="amb-saved-empty">
              Nessun ambassador salvato. Cerca dalla tab "Ricerca & Lista" e usa "Salva selezionati in Ambassador Salvati".
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2" data-testid="amb-saved-toolbar">
                <SortSelect options={sortOptions} value={savedSort} onChange={setSavedSort} testid="amb-sort-select" className="h-9 w-56 text-xs" />
                <Select value={filterDiscipline} onValueChange={setFilterDiscipline}>
                  <SelectTrigger className="h-9 w-[175px] text-xs" data-testid="amb-filter-discipline"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le discipline</SelectItem>
                    {disciplines.map((d) => <SelectItem key={d.value} value={d.value} className="capitalize">{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={genderFilter} onValueChange={setGenderFilter}>
                  <SelectTrigger className="h-9 w-[130px] text-xs" data-testid="amb-saved-filter-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="donna">Donne</SelectItem>
                    <SelectItem value="uomo">Uomini</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-9 w-[155px] text-xs" data-testid="amb-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="ml-auto text-xs text-muted-foreground" data-testid="amb-saved-count">{savedRows.length} di {savedList.length}</span>
              </div>
              {/* Invio massivo Email/WhatsApp — stesso motore dei Rivenditori */}
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-brema-card p-3" data-testid="amb-saved-bulk-toolbar">
                <Button variant="secondary" size="sm" data-testid="amb-saved-select-all-btn" onClick={toggleSavedAll}
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  {savedAllSelected ? "Deseleziona tutti" : "Seleziona tutti"}
                </Button>
                <span className="text-sm text-foreground" data-testid="amb-saved-selected-counter">
                  {savedSelCount} {savedSelCount === 1 ? "ambassador selezionato" : "ambassador selezionati"}
                </span>
                <ChannelToggle value={savedBulkChannel} onChange={setSavedBulkChannel} testidPrefix="amb-bulk" />
                {savedBulkChannel === "whatsapp" ? (
                  <Button variant="secondary" size="sm" data-testid="amb-saved-select-wa-btn" onClick={selectSavedOnlyWa}
                    className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                    Solo con WhatsApp ({savedRows.filter((a) => contactWaNumber(a)).length})
                  </Button>
                ) : (
                  <Button variant="secondary" size="sm" data-testid="amb-saved-select-email-btn" onClick={selectSavedOnlyEmail}
                    className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                    Solo con e-mail ({savedRows.filter((a) => /^\S+@\S+\.\S+$/.test(String(a.email || "").trim())).length})
                  </Button>
                )}
                <Button data-testid="amb-saved-bulk-send-btn" disabled={savedSelCount === 0} onClick={() => setSavedBulkOpen(true)}
                  className={`ml-auto rounded-full font-semibold disabled:opacity-40 ${savedBulkChannel === "whatsapp" ? "bg-[#25D366] text-white hover:bg-[#1FB855]" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
                  {savedBulkChannel === "whatsapp" ? <MessageCircle className="mr-2 h-4 w-4" /> : <Mail className="mr-2 h-4 w-4" />}
                  Invia {savedBulkChannel === "whatsapp" ? "WhatsApp" : "E-mail"} ai selezionati ({savedSelCount})
                </Button>
              </div>
              {savedRows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-8 text-center text-sm text-muted-foreground" data-testid="amb-saved-empty-filtered">Nessun ambassador con questi filtri.</div>
              ) : (
              <div className="overflow-x-auto rounded-xl border border-border bg-brema-card" data-testid="amb-saved-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="w-10 px-3 py-3">
                      <Checkbox checked={savedAllSelected} onCheckedChange={toggleSavedAll} data-testid="amb-saved-select-all-checkbox"
                        className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                    </th>
                    <th className="px-4 py-3 font-medium">Profilo</th>
                    <th className="px-4 py-3 font-medium">Follower</th>
                    <th className="px-4 py-3 font-medium">Engag.</th>
                    <th className="px-4 py-3 font-medium">Nazione</th>
                    <th className="px-4 py-3 font-medium">Recapiti</th>
                    <th className="px-4 py-3 font-medium">Stato</th>
                    <th className="px-4 py-3 font-medium">Note / Accordi</th>
                    <th className="px-4 py-3 font-medium text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {savedRows.map((amb) => (
                    <tr key={amb._id} data-testid="amb-saved-row" className={`border-b border-border last:border-0 hover:bg-muted ${savedSel.has(amb._id) ? "bg-primary/5" : ""}`}>
                      <td className="px-3 py-3">
                        <Checkbox checked={savedSel.has(amb._id)} onCheckedChange={() => toggleSavedSel(amb._id)} data-testid={`amb-saved-checkbox-${amb._id}`}
                          className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <button type="button" onClick={() => openProfile(amb)} className="shrink-0" data-testid="amb-saved-avatar-btn" title="Apri dettaglio">
                            <AmbAvatar amb={amb} size={34} />
                          </button>
                          <span className="min-w-0">
                            <button type="button" onClick={() => openProfile(amb)} data-testid="amb-saved-profile-link"
                              className="block truncate text-left font-medium text-foreground hover:text-brand">{amb.name || "Ambassador"}</button>
                            <ProfileHandle amb={amb} testid="amb-saved-handle-link"
                              className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground hover:text-brand hover:underline" />
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-foreground">{Number(amb.followers || 0).toLocaleString("it-IT")}</td>
                      <td className="whitespace-nowrap px-4 py-3 font-semibold text-brand">{amb.engagement_rate ?? 0}%</td>
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{amb.nation || amb.region || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <div className="min-w-0 max-w-[200px] text-xs">
                            <div className={`truncate ${amb.email ? "text-foreground" : "text-muted-foreground"}`} data-testid="amb-saved-email" title={amb.email || ""}>
                              {amb.email || "— email"}
                            </div>
                            <div className={`truncate ${(amb.whatsapp || amb.wa_number) ? "text-foreground" : "text-muted-foreground"}`} data-testid="amb-saved-whatsapp">
                              {amb.whatsapp || amb.wa_number || "— WhatsApp"}
                            </div>
                          </div>
                          <button type="button" onClick={() => openProfile(amb)} title="Modifica recapiti (email / WhatsApp)" data-testid="amb-saved-edit-contacts-btn"
                            className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-brema-hover hover:text-brand">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={amb.status || "nuovo"} onValueChange={(v) => changeStatus(amb, v)}>
                          <SelectTrigger className="h-8 w-36" data-testid="amb-saved-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          data-testid="amb-saved-notes-input"
                          className="h-8 min-w-[180px] text-xs"
                          placeholder="Aggiungi note/accordi…"
                          value={notesDraft[amb._id] !== undefined ? notesDraft[amb._id] : (amb.notes || "")}
                          onChange={(e) => setNotesDraft((p) => ({ ...p, [amb._id]: e.target.value }))}
                          onBlur={(e) => saveNotes(amb, e.target.value)}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="secondary" data-testid="amb-saved-detail-btn" onClick={() => openProfile(amb)}
                            className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                            <Eye className="mr-1 h-3.5 w-3.5" /> Dettaglio
                          </Button>
                          <Button size="sm" data-testid="amb-saved-contact-btn" onClick={() => openMessage(amb)}
                            className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                            <Send className="mr-1 h-3.5 w-3.5" /> Contatta
                          </Button>
                          <Button size="sm" variant="danger" data-testid="amb-saved-delete-btn" onClick={() => setAmbToDelete(amb)}
                            title="Elimina ambassador"
                            className="rounded-full text-xs">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ================= TAB STORICO CAMPAGNE ================= */}
        <TabsContent value="storico">
          {/* Report Ambassador — riepilogo per stato */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="amb-status-report">
            {STATUS_META.map((s) => (
              <div key={s.key} data-testid={`amb-status-count-${s.key.replace(/\s+/g, "-")}`}
                className="rounded-xl border border-border bg-brema-card p-4">
                <div className="mb-1 flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</span>
                </div>
                <p className={`text-2xl font-bold ${s.text}`}>{statusCounts[s.key]}</p>
              </div>
            ))}
          </div>

          {campaigns.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-8 text-center text-sm text-muted-foreground" data-testid="amb-campaigns-empty">
              Nessuna campagna di contatto generata (messaggi AI singoli o massivi). Le campagne Email e WhatsApp inviate sono elencate qui sotto.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border bg-brema-card" data-testid="amb-campaigns-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Data</th>
                    <th className="px-4 py-3 font-medium">Nome Campagna</th>
                    <th className="px-4 py-3 font-medium">Tipo</th>
                    <th className="px-4 py-3 font-medium">Filtro</th>
                    <th className="px-4 py-3 font-medium">N° Ambassador</th>
                    <th className="px-4 py-3 font-medium text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c._id} data-testid="amb-campaign-row" onClick={() => openCampaign(c)}
                      className="cursor-pointer border-b border-border last:border-0 hover:bg-muted">
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDateTime(c.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-foreground">{c.title}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium capitalize text-foreground">
                          {c.kind === "bulk" ? "Massiva" : "Singola"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {[c.discipline, c.nation, c.region].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="px-4 py-3 font-semibold text-foreground">{c.ambassador_count}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="secondary" data-testid="amb-campaign-rename-btn" onClick={() => renameCampaign(c)}
                            className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="danger" data-testid="amb-campaign-delete-btn" onClick={() => deleteCampaign(c)}
                            className="rounded-full text-xs">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-6">
            <CampaignBatchesPanel target="ambassadors" refreshKey={batchesRefresh} testidPrefix="amb-batches" onLoaded={setBatchCount} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Invio massivo Email/WhatsApp agli Ambassador salvati (stesso motore dei Rivenditori) */}
      <BulkCampaignComposer open={savedBulkOpen} onOpenChange={setSavedBulkOpen} channel={savedBulkChannel} target="ambassadors"
        recipients={savedBulkRecipients} integrations={integrations} testidPrefix="amb-bulk"
        onSent={() => { setSavedSel(new Set()); setBatchesRefresh((n) => n + 1); load(); loadCampaigns(); setTab("storico"); }} />

      {/* Single-contact dialog (DM only — email ambassadors use the composer) */}
      <Dialog open={!!dialogAmb} onOpenChange={(o) => !o && setDialogAmb(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-border bg-brema-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">Contatta · {dialogAmb?.name}</DialogTitle>
            <DialogDescription>Scegli il canale di contatto per questo ambassador.</DialogDescription>
          </DialogHeader>

          {/* Selettore canale */}
          <div className="grid grid-cols-3 gap-2" data-testid="amb-channel-selector">
            <button type="button" data-testid="amb-channel-email" onClick={() => setContactChannel("email")}
              className={`flex items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium transition-colors ${contactChannel === "email" ? "border-brand bg-brand text-white" : "border-border bg-transparent text-muted-foreground hover:bg-brema-hover"}`}>
              <Mail className="h-3.5 w-3.5" /> Email
            </button>
            <button type="button" data-testid="amb-channel-whatsapp" onClick={() => setContactChannel("whatsapp")}
              className={`flex items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium transition-colors ${contactChannel === "whatsapp" ? "border-[#25D366] bg-[#25D366] text-white" : "border-border bg-transparent text-muted-foreground hover:bg-brema-hover"}`}>
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </button>
            <button type="button" data-testid="amb-channel-instagram" onClick={() => setContactChannel("instagram")}
              className={`flex items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium transition-colors ${contactChannel === "instagram" ? "border-brand bg-brand text-white" : "border-border bg-transparent text-muted-foreground hover:bg-brema-hover"}`}>
              <Instagram className="h-3.5 w-3.5" /> Instagram
            </button>
          </div>

          {/* EMAIL */}
          {contactChannel === "email" && (
            <div className="space-y-3" data-testid="amb-email-panel">
              <p className="text-sm text-muted-foreground">
                {dialogAmb?.email
                  ? <>Invia una email a <strong className="text-foreground">{dialogAmb.email}</strong> con generazione AI, allegati e firma Brema.</>
                  : "Questo ambassador non ha una email. Aggiungine una dal profilo, oppure usa WhatsApp/Instagram."}
              </p>
              <Button type="button" disabled={!dialogAmb?.email} data-testid="amb-open-email-composer"
                onClick={() => { const a = dialogAmb; setDialogAmb(null); setComposerChannel("email"); setComposerAmb(a); }}
                className="w-full rounded-full bg-brand text-sm font-semibold text-white hover:bg-brand/90 disabled:opacity-40">
                <Mail className="mr-2 h-4 w-4" /> Apri compositore email
              </Button>
            </div>
          )}

          {/* WHATSAPP → compositore unificato (template Meta + parametri + anteprima + allegato), come negli altri moduli */}
          {contactChannel === "whatsapp" && (
            <div className="space-y-3" data-testid="amb-whatsapp-panel">
              <p className="text-sm text-muted-foreground">
                {dialogAmb?.whatsapp
                  ? <>Invia un WhatsApp a <strong className="text-foreground">{dialogAmb.whatsapp}</strong>: scegli il modello dall'Archivio, personalizza i parametri e controlla l'<strong>anteprima</strong> prima dell'invio.</>
                  : <>Nessun numero salvato: potrai inserirlo nel compositore (oppure salvarlo dal pop-up <strong>Dettaglio</strong>).</>}
              </p>
              {!integrations?.whatsapp && (
                <p className="text-xs text-red-500" data-testid="amb-wa-simulated-note">Invio simulato: i secret WhatsApp non sono ancora configurati.</p>
              )}
              <Button type="button" data-testid="amb-open-whatsapp-composer"
                onClick={() => { const a = dialogAmb; setDialogAmb(null); setComposerChannel("whatsapp"); setComposerAmb(a); }}
                className="w-full rounded-full bg-[#25D366] text-sm font-semibold text-white hover:bg-[#1FB855]">
                <MessageCircle className="mr-2 h-4 w-4" /> Apri compositore WhatsApp
              </Button>
            </div>
          )}

          {/* INSTAGRAM DM */}
          {contactChannel === "instagram" && (
            <div className="space-y-3" data-testid="amb-instagram-panel">
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="amb-channel-dm">
                <Instagram className="h-3.5 w-3.5 text-brand" /> Canale <strong>DM Instagram</strong> · copia e incolla nel profilo (anti-ban).
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Carica da Archivio Modelli (Moduli AI inclusi)</Label>
                <Select value={igTemplateId} onValueChange={applyIgTemplate} disabled={ambTemplates.length === 0}>
                  <SelectTrigger data-testid="amb-ig-template-select"><SelectValue placeholder={ambTemplates.length ? "Scegli un modello…" : "Nessun modello nell'Archivio per il modulo Ambassador"} /></SelectTrigger>
                  <SelectContent>
                    {ambTemplates.map((t) => <SelectItem key={t._id} value={t._id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                {ambTemplates.length === 0 && (
                  <p className="text-[11px] text-muted-foreground" data-testid="amb-ig-no-templates">Crea un modello nel Template Center (modulo Ambassador) o in Moduli AI per caricarlo qui.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Lingua (per la generazione AI e la traduzione)</Label>
                <Select value={msgLanguage} onValueChange={setMsgLanguage}>
                  <SelectTrigger data-testid="single-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generateMessage} disabled={generating} variant="secondary" data-testid="generate-message-btn"
                className="w-full rounded-full border border-border bg-transparent font-semibold hover:bg-brema-hover">
                {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generazione…</> : <><Sparkles className="mr-2 h-4 w-4 text-brand" /> Genera bozza con AI</>}
              </Button>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Messaggio · scrivi, incolla o modifica il testo</Label>
                <Textarea data-testid="amb-message-textarea" value={message} onChange={(e) => setMessage(e.target.value)}
                  className="min-h-[150px] text-sm"
                  placeholder="Scrivi qui il tuo messaggio personalizzato, oppure genera una bozza con l'AI e modificala liberamente…" />
                <TranslateControls text={message} onApply={({ text }) => setMessage(text)} lang={msgLanguage} showSelect={false}
                  nation={dialogAmb?.nation} testidPrefix="amb-translate"
                  label={msgLanguage === "auto" ? "Traduci nella lingua del profilo" : `Traduci in ${LANG_LABEL[msgLanguage] || msgLanguage}`} />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={copyForDM} disabled={!message.trim()} data-testid="amb-copy-dm-btn"
                  className="rounded-full bg-brand text-xs font-semibold text-white hover:bg-brand/90 disabled:opacity-40">
                  <Instagram className="mr-1 h-3.5 w-3.5" /> Copia testo per DM
                </Button>
                <Button type="button" onClick={saveManualMessage} disabled={savingMsg || !message.trim()} variant="secondary" size="sm" data-testid="amb-save-message-btn"
                  className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                  {savingMsg ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Salva nello storico
                </Button>
                <Button type="button" variant="secondary" size="sm" data-testid="amb-copy-btn" disabled={!message.trim()}
                  onClick={() => { navigator.clipboard.writeText(message); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                  className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                  {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                  {copied ? "Copiato" : "Copia"}
                </Button>
                {profileUrl(dialogAmb) && (
                  <a href={profileUrl(dialogAmb)} target="_blank" rel="noopener noreferrer" data-testid="amb-open-profile-single">
                    <Button type="button" variant="secondary" size="sm"
                      className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                      <ExternalLink className="mr-1 h-3.5 w-3.5" /> Apri profilo
                    </Button>
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ambassador unified composer: Email (AI/modelli/allegati/anteprima) o WhatsApp Business (template + parametri + anteprima) */}
      <EmailComposer
        open={!!composerAmb}
        onOpenChange={(o) => !o && setComposerAmb(null)}
        target="ambassador"
        contact={composerAmb}
        templateModule="ambassador"
        defaultTo={composerAmb?.email || ""}
        allowDraft={false}
        allowWhatsapp
        initialChannel={composerChannel}
        waDefaults={{
          phone: composerAmb?.whatsapp || composerAmb?.wa_number || "",
          name: composerAmb?.name || "",
          link: integrations?.whatsapp_catalog_link || "",
          template: integrations?.whatsapp_template || "",
          nation: composerAmb?.nation || "",
        }}
        waTemplates={waTemplates}
        title={`Contatta · ${composerAmb?.name || ""}`}
        channelHint="Scegli il canale: E-mail (AI, Archivio Modelli, allegati, anteprima con firma) oppure WhatsApp Business (modello di primo contatto con anteprima)."
        onSent={() => { setComposerAmb(null); load(); loadCampaigns(); }}
      />

      {/* Manual ambassador addition dialog */}
      <ImportContactsDialog open={importOpen} onOpenChange={setImportOpen} section="ambassadors" onDone={load} onViewAll={showImported} />
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto border-border bg-brema-card" data-testid="manual-ambassador-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Aggiungi ambassador manuale</DialogTitle>
            <DialogDescription>
              Solo il nome è obbligatorio. Se inserisci un'<strong>email</strong> il contatto userà il canale email (con anteprima e firma); altrimenti resta il DM Instagram.
            </DialogDescription>
          </DialogHeader>
          <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <Label className="text-xs font-semibold text-primary">✨ Importazione rapida per nome</Label>
            <p className="mb-2 mt-0.5 text-xs text-muted-foreground">Digita il nome: l'AI trova il profilo reale ({platform}), verifica l'handle e compila i dati. Poi controlla e salva.</p>
            <div className="flex gap-2">
              <Input data-testid="amb-lookup-input" value={lookupName} onChange={(e) => setLookupName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runAmbLookup(); } }}
                placeholder="es. Luca Braidot, Marco Fontana…" />
              <Button type="button" onClick={runAmbLookup} disabled={lookingUp} data-testid="amb-lookup-btn"
                className="shrink-0 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {lookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1.5 h-4 w-4" /> Cerca con AI</>}
              </Button>
            </div>
            {lookupNote && (
              <p data-testid="amb-lookup-note" className={`mt-2 text-xs font-medium ${lookupVerified ? "text-emerald-600" : "text-amber-600"}`}>{lookupNote}</p>
            )}
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input data-testid="manual-amb-name" value={manualAmb.name} onChange={(e) => setMA({ name: e.target.value })} placeholder="es. Marco Rossi" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>@handle Instagram</Label>
                <Input data-testid="manual-amb-handle" value={manualAmb.handle} onChange={(e) => setMA({ handle: e.target.value })} placeholder="@marcorossi" />
              </div>
              <div className="space-y-1.5">
                <Label>Follower</Label>
                <Input data-testid="manual-amb-followers" type="number" min={0} value={manualAmb.followers} onChange={(e) => setMA({ followers: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-brand" /> Email <span className="text-muted-foreground">(opzionale → attiva canale email)</span>
              </Label>
              <Input data-testid="manual-amb-email" type="email" value={manualAmb.email} onChange={(e) => setMA({ email: e.target.value })} placeholder="marco@email.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> Numero WhatsApp <span className="text-muted-foreground">(opzionale → attiva canale WhatsApp)</span>
              </Label>
              <Input data-testid="manual-amb-whatsapp" value={manualAmb.whatsapp}
                onChange={(e) => setMA({ whatsapp: e.target.value })}
                onBlur={() => { const n = normalizePhoneE164(manualAmb.whatsapp, manualAmb.nation); if (n) setMA({ whatsapp: n }); }}
                placeholder="+39 333 1234567" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Disciplina</Label>
                <Select value={manualAmb.discipline} onValueChange={(v) => setMA({ discipline: v })}>
                  <SelectTrigger data-testid="manual-amb-discipline"><SelectValue /></SelectTrigger>
                  <SelectContent>{disciplines.map((d) => <SelectItem key={d.value} value={d.value} className="capitalize">{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Nazione</Label>
                <Select value={manualAmb.nation} onValueChange={(v) => setMA({ nation: v })}>
                  <SelectTrigger data-testid="manual-amb-nation"><SelectValue /></SelectTrigger>
                  <SelectContent>{NATIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Note / Accordi</Label>
              <Textarea data-testid="manual-amb-notes" value={manualAmb.notes} onChange={(e) => setMA({ notes: e.target.value })} className="min-h-[70px]" placeholder="Es. contatto da fiera, disponibile per barter…" />
            </div>
            <Button onClick={submitManualAmbassador} disabled={manualSaving} data-testid="manual-amb-submit"
              className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {manualSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva ambassador</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Assisted mass outreach dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkItems([]); } }}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid="bulk-outreach-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <MessageSquare className="h-5 w-5 text-brand" /> Contatto massivo assistito
            </DialogTitle>
            <DialogDescription>
              Copia il messaggio e aprilo direttamente nel profilo Instagram. Nessun invio automatico:
              incolli tu il DM per evitare blocchi/ban dell'account. La campagna è salvata nello Storico.
            </DialogDescription>
          </DialogHeader>

          {bulkLoading ? (
            <div className="flex flex-col items-center gap-3 py-12 text-foreground" data-testid="bulk-loading">
              <Loader2 className="h-6 w-6 animate-spin text-brand" />
              Generazione di {selected.size} messaggi personalizzati…
            </div>
          ) : (
            <div className="space-y-4">
              {bulkItems.map((it) => (
                <div key={it.ambassador_id} data-testid="bulk-outreach-item"
                  className="rounded-lg border border-border bg-brema-bg p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Instagram className="h-4 w-4 text-brand" />
                      {it.name} <span className="text-muted-foreground">@{it.handle}</span>
                    </div>
                  </div>
                  {it.message ? (
                    <p className="whitespace-pre-line rounded-md bg-black/20 p-3 text-sm text-foreground" data-testid="bulk-item-message">
                      {it.message}
                    </p>
                  ) : (
                    <p className="rounded-md bg-black/20 p-3 text-sm text-red-300">Generazione fallita per questo profilo.</p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <Button type="button" variant="secondary" size="sm" data-testid="bulk-copy-btn"
                      disabled={!it.message} onClick={() => copyText(it.message, it.ambassador_id)}
                      className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                      {copiedId === it.ambassador_id ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                      {copiedId === it.ambassador_id ? "Copiato" : "Copia testo"}
                    </Button>
                    <a href={it.url || igUrlAmb(it.handle)} target="_blank" rel="noopener noreferrer" data-testid="bulk-open-profile-btn">
                      <Button type="button" size="sm"
                        className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                        <ExternalLink className="mr-1 h-3.5 w-3.5" /> Apri profilo Instagram
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
              <p className="flex items-center gap-1.5 pt-1 text-xs text-muted-foreground">
                <Globe className="h-3.5 w-3.5" /> Suggerimento: distribuisci gli invii nel tempo per proteggere l'account.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Profile detail dialog */}
      <Dialog open={!!profileDetail} onOpenChange={(o) => !o && setProfileDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto border-border bg-brema-card" data-testid="amb-profile-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground" data-testid="amb-profile-title">Dettaglio &amp; Note Accordi</DialogTitle>
            <DialogDescription>
              Imposta lo stato della trattativa e annota gli accordi presi con questo profilo.
            </DialogDescription>
          </DialogHeader>
          {profileDetail && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <AmbAvatar amb={profileDetail.ambassador} size={56} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold text-foreground" data-testid="amb-profile-name">{profileDetail.ambassador?.name || "Ambassador"}</p>
                    <StatusBadge status={profileDetail.ambassador?.status || "nuovo"} />
                  </div>
                  <ProfileHandle amb={profileDetail.ambassador} testid="amb-profile-handle"
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-brand">
                    {profileDetail.ambassador?.platform === "strava" ? <Activity className="h-3 w-3" /> : <Instagram className="h-3 w-3" />}
                    @{profileDetail.ambassador?.handle || "n-d"} {profileUrl(profileDetail.ambassador) && <ExternalLink className="h-3 w-3" />}
                  </ProfileHandle>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-brema-bg py-2">
                  <p className="text-sm font-bold text-foreground">{Number(profileDetail.ambassador?.followers || 0).toLocaleString("it-IT")}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Follower</p>
                </div>
                <div className="rounded-lg bg-brema-bg py-2">
                  <p className="text-sm font-bold text-brand">{profileDetail.ambassador?.engagement_rate ?? 0}%</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Engagement</p>
                </div>
                <div className="rounded-lg bg-brema-bg py-2">
                  <p className="text-xs font-semibold text-foreground">{profileDetail.ambassador?.nation || profileDetail.ambassador?.region || "N/D"}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Zona</p>
                </div>
              </div>
              {profileDetail.ambassador?.bio && <p className="text-sm text-foreground">{profileDetail.ambassador.bio}</p>}
              <AmbassadorContactsEditor amb={profileDetail.ambassador}
                onSaved={(upd) => {
                  setProfileDetail((p) => (p ? { ...p, ambassador: { ...p.ambassador, email: upd.email, whatsapp: upd.whatsapp } } : p));
                  setList((prev) => prev.map((a) => (a._id === upd._id ? { ...a, email: upd.email, whatsapp: upd.whatsapp } : a)));
                }} />
              <AmbQualificationDetails amb={profileDetail.ambassador} />

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Stato</Label>
                  <Select value={profileDetail.ambassador?.status || "nuovo"} onValueChange={(v) => changeStatus(profileDetail.ambassador, v)}>
                    <SelectTrigger className="h-8 w-40" data-testid="amb-profile-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Note &amp; Accordi — es. barter (2 capi + 3 post/reel), sconti, scadenze</Label>
                  <Textarea data-testid="amb-profile-notes" className="min-h-[80px] text-sm"
                    defaultValue={profileDetail.ambassador?.notes || ""}
                    onBlur={(e) => saveNotes(profileDetail.ambassador, e.target.value)}
                    placeholder="Es. Barter 2 capi + 3 post reel · sconto 30% · da richiamare a settembre" />
                </div>
              </div>

              <div>
                <p className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <MessageSquare className="h-3.5 w-3.5" /> Cronologia messaggi ({profileDetail.messages?.length || 0})
                </p>
                {profileLoading ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
                ) : (profileDetail.messages || []).length === 0 ? (
                  <p className="rounded-lg bg-brema-bg p-3 text-sm text-muted-foreground" data-testid="amb-profile-no-msg">Nessun messaggio ancora generato per questo profilo.</p>
                ) : (
                  <div className="space-y-2">
                    {profileDetail.messages.map((m) => (
                      <div key={m._id} data-testid="amb-profile-msg" className="rounded-lg border border-border bg-brema-bg p-3">
                        <div className="mb-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                          <Clock className="h-3 w-3" /> {fmtDateTime(m.created_at)}
                        </div>
                        <MessageBody text={m.message} testId="amb-profile-msg-body" />
                        <Button type="button" variant="secondary" size="sm" data-testid="amb-profile-msg-copy"
                          onClick={() => copyText(m.message, m._id)}
                          className="mt-2 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                          {copiedId === m._id ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                          {copiedId === m._id ? "Copiato" : "Copia"}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete ambassador confirm */}
      <Dialog open={!!ambToDelete} onOpenChange={(o) => !o && setAmbToDelete(null)}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="amb-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Elimina ambassador</DialogTitle>
            <DialogDescription>
              Vuoi eliminare <strong className="text-foreground">{ambToDelete?.name}</strong> dalla lista salvati? L'azione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" data-testid="amb-delete-cancel" onClick={() => setAmbToDelete(null)}
              className="rounded-full border border-border bg-transparent">Annulla</Button>
            <Button data-testid="amb-delete-confirm-btn" disabled={deletingAmb} onClick={deleteAmbassador}
              className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">
              {deletingAmb ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Elimina</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk archive/delete confirm */}
      <AlertDialog open={!!bulkAction} onOpenChange={(o) => !o && !bulkActionLoading && setBulkAction(null)}>
        <AlertDialogContent data-testid="amb-results-action-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovi dai risultati di ricerca</AlertDialogTitle>
            <AlertDialogDescription>
              {`Vuoi rimuovere ${selected.size} profili dai risultati di ricerca? I risultati sono temporanei: verranno scartati, mentre l'archivio "Ambassador Salvati" non viene toccato.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="amb-results-action-confirm-cancel" disabled={bulkActionLoading}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="amb-results-action-confirm-btn" disabled={bulkActionLoading}
              onClick={(e) => { e.preventDefault(); deleteSelected(); }}
              className="bg-brema-red text-white hover:bg-brema-red/90">
              {bulkActionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rimuovi"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Campaign detail dialog */}
      <Dialog open={!!campaignDetail} onOpenChange={(o) => !o && setCampaignDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid="amb-campaign-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              {campaignDetail?.campaign?.title}
              <button onClick={() => renameCampaign(campaignDetail.campaign)} data-testid="amb-campaign-detail-rename" className="text-muted-foreground hover:text-brand">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-2">
              <span>{fmtDateTime(campaignDetail?.campaign?.created_at)}</span>
              <span>·</span>
              <span className="capitalize">{campaignDetail?.campaign?.kind === "bulk" ? "Massiva" : "Singola"}</span>
              <span>·</span>
              <span>{campaignDetail?.campaign?.ambassador_count} ambassador</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(campaignDetail?.messages || []).map((m) => (
              <div key={m._id} data-testid="amb-campaign-msg" className="rounded-lg border border-border bg-brema-bg p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Instagram className="h-4 w-4 text-brand" /> {m.ambassador_name} <span className="text-muted-foreground">@{m.handle}</span>
                  </div>
                  <a href={m.url || igUrlAmb(m.handle)} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-brand">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
                <MessageBody text={m.message} className="rounded-md bg-black/20 p-3" testId="amb-campaign-msg-body" />
                <Button type="button" variant="secondary" size="sm" data-testid="amb-campaign-msg-copy"
                  onClick={() => copyText(m.message, m._id)}
                  className="mt-2 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                  {copiedId === m._id ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                  {copiedId === m._id ? "Copiato" : "Copia testo"}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
